import { cn, deriveTrackName } from "@design-system/utils";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";
import * as React from "react";

import { sheetVariants } from "./sheet-variants";

/**
 * Non-modal sheet panel — same visual styling as SheetContent but a fundamentally
 * different ARIA contract. Key differences vs Sheet / SheetContent:
 *
 * | Concern            | Sheet (SheetContent)                          | SheetPanel                                      |
 * |--------------------|-----------------------------------------------|-------------------------------------------------|
 * | Role               | `role="dialog"` (set by Radix)                | `<aside>` → implicit `role="complementary"`     |
 * | aria-modal         | `aria-modal="true"` (set by Radix)            | Not present — background stays reachable        |
 * | Focus trap         | Yes — Radix traps Tab within the dialog       | No — Tab flows naturally through the document   |
 * | aria-labelledby    | Auto-wired to <SheetTitle> by Radix           | Caller must pass `aria-labelledby` / `aria-label` |
 * | Overlay            | Renders <SheetOverlay> (blocks background)    | None — page scrolls and is fully interactive    |
 * | Portal             | Rendered outside DOM in a Radix portal        | Rendered in normal document order               |
 * | Escape to close    | Radix closes on Escape automatically          | Caller handles closing (e.g. DataTableSheet)    |
 *
 * Use SheetPanel when the panel is complementary to, not replacing, the current
 * context — e.g. a master/detail split where the table stays usable alongside it.
 * Use Sheet when the overlay is intentional and focus must be confined (modals,
 * confirmations, forms that shouldn't be skipped).
 */

interface SheetPanelProps extends React.ComponentProps<"aside"> {
  open: boolean;
  side?: "top" | "right" | "bottom" | "left";
}

function SheetPanel({
  open,
  side = "right",
  className,
  children,
  ...props
}: Readonly<SheetPanelProps>) {
  if (!open) return null;

  return (
    <aside
      data-slot="sheet-panel"
      className={cn(sheetVariants({ kind: "panel", side }), className)}
      {...props}
    >
      {children}
    </aside>
  );
}

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

function SheetTrigger({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetClose({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

function SheetPortal({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
}
const SheetOverlay = React.forwardRef<
  React.ComponentRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    ref={ref}
    data-slot="sheet-overlay"
    className={cn(
      "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-100 bg-black/50",
      className
    )}
    {...props}
  />
));
SheetOverlay.displayName = "SheetOverlay";

const SheetContent = React.forwardRef<
  React.ComponentRef<typeof SheetPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content> & {
    side?: "top" | "right" | "bottom" | "left";
    withOverlay?: boolean;
    portal?: boolean;
    hideCloseButton?: boolean;
  }
>(
  (
    {
      className,
      children,
      side = "right",
      withOverlay = true,
      portal = true,
      hideCloseButton = false,
      ...props
    },
    ref
  ) => {
    const content = (
      <>
        {withOverlay && <SheetOverlay />}
        <SheetPrimitive.Content
          ref={ref}
          data-slot="sheet-content"
          className={cn(sheetVariants({ kind: "modal", side }), className)}
          {...props}
        >
          {children}
          {!hideCloseButton && (
            <SheetPrimitive.Close
              aria-label="Close"
              className="ring-offset-background focus:ring-ring data-[state=open]:bg-secondary absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none"
              {...deriveTrackName(props, "-close")}
            >
              <XIcon className="pointer-events-none size-4" />
            </SheetPrimitive.Close>
          )}
        </SheetPrimitive.Content>
      </>
    );

    return portal ? <SheetPortal>{content}</SheetPortal> : content;
  }
);
SheetContent.displayName = "SheetContent";

function SheetPanelTitle({
  className,
  children,
  ...props
}: React.ComponentProps<"h2">) {
  return (
    <h2
      data-slot="sheet-panel-title"
      className={cn("text-foreground text-lg font-semibold", className)}
      {...props}
    >
      {children}
    </h2>
  );
}

function SheetPanelClose({
  className,
  ...props
}: React.ComponentProps<"button">) {
  return (
    <button
      type="button"
      aria-label="Close"
      data-slot="sheet-panel-close"
      className={cn(
        "ring-offset-background focus:ring-ring absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none",
        className
      )}
      {...props}
    >
      <XIcon className="pointer-events-none size-4" />
    </button>
  );
}

function SheetHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-header"
      className={cn("flex flex-col gap-1.5 py-6", className)}
      {...props}
    />
  );
}

function SheetFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn("mt-auto flex flex-col gap-2 p-4", className)}
      {...props}
    />
  );
}

function SheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn("text-foreground text-lg font-semibold", className)}
      {...props}
    />
  );
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetPanel,
  SheetPanelTitle,
  SheetPanelClose,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
